/**
 * Сервис для клиентских действий (скачивание, шеринг)
 */

import { getWellandStoredToken } from '../../auth/storageKeys';
import { refreshAccessToken } from '../../auth/refreshAccessToken';

/**
 * Инициирует скачивание Blob файла в браузере.
 * @param {Blob} pdfBlob - Blob PDF файла
 * @param {string} filename - Имя файла
 */
export function downloadPDF(pdfBlob, filename = 'equipment-configuration.pdf') {
    try {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
            if (link.parentNode) document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);

    } catch (error) {
        console.error('❌ Ошибка скачивания PDF:', error);
        throw error;
    }
}

function getAuthToken() {
    return getWellandStoredToken();
}

/**
 * Из заголовка Content-Disposition (как на /api/project-pdf/:id/file).
 */
export function parseContentDispositionFilename(disposition) {
    if (!disposition || typeof disposition !== 'string') return null;
    const utf8Star = disposition.match(/filename\*\s*=\s*([\w-]+)''([^;\n]+)/i);
    if (utf8Star) {
        try {
            return decodeURIComponent(utf8Star[2].trim());
        } catch (_) {
            /* fall through */
        }
    }
    const generalStar = disposition.match(/filename\*\s*=\s*([^']+)'([^']*)'([^;\n]+)/);
    if (generalStar) {
        try {
            return decodeURIComponent(generalStar[3].trim());
        } catch (_) {
            /* fall through */
        }
    }
    const quoted = disposition.match(/filename\s*=\s*"((?:[^"\\]|\\.)*)"/i);
    if (quoted && quoted[1]) return quoted[1].replace(/\\(.)/g, '$1');
    const simple = disposition.match(/filename\s*=\s*([^;\n]+)/i);
    if (simple && simple[1]) return simple[1].trim().replace(/^"(.*)"$/, '$1');
    return null;
}

function triggerBrowserDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        if (link.parentNode) document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, 100);
}

export async function downloadPDFByUrl(pdfUrl, fallbackFilename = 'welland-create.pdf') {
    try {
        const doFetch = (accessToken) =>
            fetch(pdfUrl, {
                method: 'GET',
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
                credentials: 'include',
            });
        let token = getAuthToken();
        let response = await doFetch(token);
        if (response.status === 401) {
            const newTok = await refreshAccessToken();
            if (newTok) {
                token = newTok;
                response = await doFetch(token);
            }
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const cd = response.headers.get('Content-Disposition');
        const filename = parseContentDispositionFilename(cd) || fallbackFilename;
        triggerBrowserDownload(blob, filename);
    } catch (error) {
        console.error('❌ Ошибка скачивания PDF по ссылке:', error);
        throw error;
    }
}

/**
 * Открывает WhatsApp с готовым сообщением.
 *
 * @param {string} pdfUrl - Ссылка на PDF (лучше share URL)
 * @param {Object} poolData - Данные бассейна
 * @param {{ popup?: Window | null }} [opts]
 */
/** Пустая вкладка в тот же клик (обход блокировщика); после await делаем location.replace на WhatsApp — без текста «готовим ссылку». */
export function openWhatsAppPopupForLater() {
    try {
        return window.open('about:blank', '_blank');
    } catch {
        return null;
    }
}

export function sendToWhatsApp(pdfUrl, poolData = {}, opts = {}) {
    const { popup = null } = opts;
    try {
        const { length = '?', width = '?', depth = '?', type = 'Скиммерный' } = poolData;

        const message = `Добрый день! 

Я сформировал подборку оборудования для бассейна:

🏊 Размеры: ${length} × ${width} × ${depth} м
💧 Тип: ${type}

📄 Коммерческое предложение:
${pdfUrl}

Хотел бы получить консультацию.`;

        const phoneNumber = '87772296225';
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;

        if (popup && typeof popup.closed === 'boolean' && !popup.closed) {
            try {
                popup.location.replace(whatsappUrl);
                return;
            } catch (e) {
                console.warn('Не удалось перенаправить окно WhatsApp, пробуем новое:', e);
            }
        }

        window.open(whatsappUrl, '_blank');
    } catch (error) {
        console.error('❌ Ошибка открытия WhatsApp:', error);
        throw error;
    }
}
